import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import { HighchartsReact, HighchartsReactRefObject } from 'highcharts-react-official';
import { stringDateWithTimeFromUtcNonNullable } from '@utils/convertor';

export const HEIGHT = 95;

type Props = {
    ref?: React.Ref<HighchartsReactRefObject>;
    options?: Pick<Highcharts.Options, 'xAxis' | 'series'>;
    width?: number;
    marginLeft?: number;
    basisDate?: Date;
    borderColor: string;
    labelColor: string;
    startingExtremes: { min: number; max: number };
};

const XAxisChart = ({
    ref,
    options = {},
    width,
    marginLeft,
    basisDate,
    borderColor,
    labelColor,
    startingExtremes,
}: Props) => {
    const { xAxis, series } = options;

    const allOptions: Highcharts.Options = useMemo(
        () => ({
            credits: {
                enabled: false,
            },
            chart: {
                height: HEIGHT,
                marginBottom: HEIGHT,
                animation: false,
                zooming: {
                    mouseWheel: false,
                },
                style: {
                    position: 'relative',
                    marginLeft,
                },
                width,
                plotBorderWidth: 0,
                plotBorderColor: 'black',
                marginTop: -1,

                events: {
                    load() {
                        const chart = this;

                        chart.axes
                            .filter((x) => x.isXAxis)
                            .forEach((x) => {
                                x.setExtremes(startingExtremes.min, startingExtremes.max);
                            });
                    },
                },
            },
            exporting: {
                enabled: false,
            },
            rangeSelector: {
                enabled: false,
            },
            scrollbar: {
                enabled: false,
            },
            navigator: {
                enabled: false,
            },
            xAxis: {
                ...xAxis,
                panningEnabled: false,
                labels: {
                    reserveSpace: false,
                    style: {
                        // @ts-expect-error Highcharts type is wrong. 'auto' is a valid value for the CSS width attribute.
                        width: 'auto' as number, // Makes labels not get truncated with ellipses
                        color: labelColor,
                    },
                    rotation: -45,
                    formatter(e) {
                        const dateTime = new Date(e.value);

                        const dateTimeText = stringDateWithTimeFromUtcNonNullable(dateTime, 'DD-MMM-YYYY HH:mm:ss')!;

                        const [dateText, timeText] = dateTimeText.split(' ');

                        const isWithinDay = timeText !== '00:00:00';

                        if (isWithinDay) {
                            // Only show hours/minutes
                            const [hoursText, minutesText] = timeText.split(':');
                            return `${hoursText}:${minutesText}`;
                        }

                        const [dayText, monthText] = dateText.split('-');
                        const formattedDateText = `${dayText}-${monthText}`;

                        if (!basisDate) {
                            return formattedDateText;
                        }

                        // get difference in days from basis date
                        const timeDiff = dateTime.getTime() - basisDate.getTime();
                        const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

                        // Text can't be center-aligned when rotated, so spaces have to be manually added to make it look centered-ish...
                        const spacesNeeded = Math.min(4, dayDiff.toString().length - 1);
                        const spaces = new Array(spacesNeeded).fill('&nbsp;').join('');

                        return `${formattedDateText}${spaces}<br/>(Day ${dayDiff})`;
                    },
                },
                visible: true,
                crosshair: {
                    width: 0,
                },
                lineColor: borderColor,
                tickColor: borderColor,
            },
            // Render 2 y axes. 1 for left, 1 for right
            yAxis: Array.from(Array(2)).map((_, i) => ({
                labels: {
                    enabled: false,
                },
                gridLineWidth: 0,

                offset: 0,
                lineWidth: 1,
                lineColor: borderColor,
                opposite: i === 1, // false = 'left', true = 'right'
            })),

            series,
        }),
        [
            basisDate,
            borderColor,
            labelColor,
            marginLeft,
            series,
            startingExtremes.max,
            startingExtremes.min,
            width,
            xAxis,
        ],
    );

    return <HighchartsReact ref={ref} highcharts={Highcharts} constructorType="stockChart" options={allOptions} />;
};

export default XAxisChart;
